import React from "react";
import { Card } from "primereact/card";
import KpiStatCard from "../../../KpiDashboard/components/KpiStatCard";
import MonthlyTimeSeries from "../../../components/graphs/MonthlyTimeSeries";
import type { DynamicsAnalytics } from "../../../../types/targetGroupAnalytics";
import { parseMonthKey } from "../utils/format";
import {
  COCHARGE_DARK_GREEN,
  ORANGE_COLOR,
  PRIMARY_COLOR,
  TEAL_COLOR,
  PURPLE_COLOR,
} from "../../../../styles/theme_constants";

interface MembershipDynamicsSectionProps {
  data: DynamicsAnalytics;
}

/** US2 — monthly membership dynamics with in-progress current month. */
const MembershipDynamicsSection: React.FC<MembershipDynamicsSectionProps> = ({ data }) => {
  if (data.series.length === 0) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: "40vh" }}
      >
        <i className="pi pi-inbox text-5xl text-color-secondary mb-3" />
        <p className="text-color-secondary">No membership activity in this scope yet.</p>
      </div>
    );
  }

  const { series } = data;
  const months = series.map((p) => parseMonthKey(p.period));
  // MonthlyTimeSeries expects finals[i] === false for the in-progress month
  const finals = series.map((p) => !p.isCurrentMonth);

  // Average tenure KPI: use last data point (most recent month)
  const lastPoint = series[series.length - 1];

  return (
    <div className="flex flex-column gap-4">
      {/* Tenure KPI */}
      <div className="grid">
        <div className="col-12 md:col-4">
          <KpiStatCard
            title="Avg membership tenure (latest)"
            value={`${Math.round(lastPoint.avgMembershipDays)} days`}
            icon="pi pi-clock"
            severity="info"
          />
        </div>
      </div>

      {/* Joins / leaves / net + new groups */}
      <div className="grid">
        <div className="col-12 lg:col-6">
          <Card className="h-full">
            <MonthlyTimeSeries
              title="Joins / leaves / net per month"
              months={months}
              finals={finals}
              series={[
                { label: "Joins",  color: COCHARGE_DARK_GREEN, data: series.map((p) => p.joins) },
                { label: "Leaves", color: ORANGE_COLOR,        data: series.map((p) => p.leaves) },
                { label: "Net",    color: PRIMARY_COLOR,       data: series.map((p) => p.netChange) },
              ]}
            />
          </Card>
        </div>
        <div className="col-12 lg:col-6">
          <Card className="h-full">
            <MonthlyTimeSeries
              title="New groups per month"
              months={months}
              finals={finals}
              series={[
                { label: "New groups", color: PRIMARY_COLOR, data: series.map((p) => p.newGroups) },
              ]}
            />
          </Card>
        </div>
      </div>

      {/* Active members + retention */}
      <div className="grid">
        <div className="col-12 lg:col-6">
          <Card className="h-full">
            <MonthlyTimeSeries
              title="Active members at month end"
              months={months}
              finals={finals}
              series={[
                { label: "Active members", color: TEAL_COLOR, data: series.map((p) => p.activeMembers) },
              ]}
            />
          </Card>
        </div>
        <div className="col-12 lg:col-6">
          <Card className="h-full">
            <MonthlyTimeSeries
              title="Member re-joins (retention)"
              months={months}
              finals={finals}
              series={[
                { label: "Re-joins", color: PURPLE_COLOR, data: series.map((p) => p.memberRetention) },
              ]}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MembershipDynamicsSection;
